import { useEffect, useRef, useState } from 'react';
import { t, type MessageKey } from '@aquabox/shared';
import { useBulkAssign, useVehicles } from '../api/queries';
import type { BulkAssignResult } from '../api/queries';
import { Icon } from './Icon';
import type { Vehicle } from '../api/types';

/** `BulkAssignResult.skipped[].reason` → Türkçe metin anahtarı. Sunucu bilinen nedenlerden
 *  birini döner; bilinmeyen/bozuk değerler için ERROR etiketi kullanılır (joker). */
export function skipReasonKey(reason: string): MessageKey {
  switch (reason) {
    case 'CONFLICT':
      return 'panel.orders.bulkAssignSkippedReason.conflict';
    case 'STALE_TRIP':
      return 'panel.orders.bulkAssignSkippedReason.staleTrip';
    case 'NOT_FOUND':
      return 'panel.orders.bulkAssignSkippedReason.notFound';
    case 'INVALID':
      return 'panel.orders.bulkAssignSkippedReason.invalid';
    case 'ERROR':
      return 'panel.orders.bulkAssignSkippedReason.error';
    default:
      return 'panel.orders.bulkAssignSkippedReason.error';
  }
}

const FOCUSABLE =
  'button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

interface Props {
  /** Atanacak siparişler — çağıran (seçim ya da bölge grubu) belirler; boş liste ile açılmaz. */
  orderIds: string[];
  /** id → listedeki satırın gösterdiği etiket; atlananlar listesinde ham id yerine kullanılır. */
  orderLabels?: Record<string, string>;
  /** region.defaultVehicleId — varsa önerilen araç olarak işaretlenir. */
  suggestedVehicleId?: string | null;
  /** Kapanışta odağı geri vermek çağıranın işi (tetikleyici DOM'dan kalkmış olabilir). */
  onClose: () => void;
}

export function BulkAssignDialog({ orderIds, orderLabels, suggestedVehicleId, onClose }: Props) {
  const vehicles = useVehicles();
  const bulkAssign = useBulkAssign();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    suggestedVehicleId ?? null,
  );
  const [result, setResult] = useState<BulkAssignResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const vehicleItems = vehicles.data?.items ?? [];
  const isLoading = bulkAssign.isPending;

  // Escape ile kapat — istek sürerken kapanmaz (sonuç kaybolmasın). Dinleyici her render'da
  // güncel `isLoading`/`onClose` ile yeniden bağlanır.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, isLoading]);

  // Açılışta panele odaklan; ilk odak kapat butonu değil panel gövdesi (kullanıcı Escape/Enter
  // ile gezinsin; tab sırası doğal akış).
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // Basit odak tuzağı: Tab/Shift+Tab dialog içindeki odaklanabilir öğeler arasında döner.
  const trapFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (nodes.length === 0) {
      e.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && (active === last || active === panelRef.current)) {
      e.preventDefault();
      first?.focus();
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const selectedPlate = selectedVehicleId
    ? (vehicleItems.find((v: Vehicle) => v.id === selectedVehicleId)?.plate ?? '')
    : '';

  const handleSubmit = () => {
    if (!selectedVehicleId || orderIds.length === 0) return;
    setErrorMessage(null);
    bulkAssign.mutate(
      { orderIds, vehicleId: selectedVehicleId },
      {
        onSuccess: (data) => {
          setResult(data);
        },
        onError: (err: unknown) => {
          setResult(null);
          // Panel deseni (Integrations `ErrorLine`): sunucunun Türkçe mesajı, yoksa genel metin.
          const message = (err as { message?: string } | null)?.message;
          setErrorMessage(message && message.length > 0 ? message : t('error.internal'));
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bad-title"
      data-testid="bulk-assign-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={trapFocus}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-card)',
          border: '1px solid var(--border)',
          padding: '28px 32px',
          width: 'min(460px, calc(100vw - 32px))',
          boxShadow: '0 16px 48px rgba(16,30,56,.18)',
          outline: 'none',
        }}
      >
        {/* Başlık */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20,
          }}
        >
          <h2 id="bad-title" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {t('panel.orders.bulkAssignTitle')}
          </h2>
          <button
            ref={closeBtnRef}
            onClick={handleClose}
            aria-label={t('common.close')}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--r-row)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        {/* Seçili sipariş sayısı + araç */}
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
          {selectedVehicleId
            ? t('panel.orders.bulkAssignConfirm', { n: orderIds.length, plate: selectedPlate })
            : t('panel.orders.bulkAssignCount', { n: orderIds.length })}
        </p>

        {/* Araç seçimi */}
        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="bulk-assign-vehicle"
            style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
          >
            {t('panel.colVehicle')}
          </label>
          <select
            id="bulk-assign-vehicle"
            className="input"
            style={{ width: '100%', height: 40 }}
            value={selectedVehicleId ?? ''}
            onChange={(e) => setSelectedVehicleId(e.target.value || null)}
          >
            <option value="">{t('panel.selectVehicle')}</option>
            {vehicleItems.map((v: Vehicle) => (
              <option key={v.id} value={v.id}>
                {v.plate}
              </option>
            ))}
          </select>
        </div>

        {/* Hata — sunucu mesajı öncelikli; genel yedek. */}
        {errorMessage && (
          <p
            role="alert"
            data-testid="bulk-assign-error"
            style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 12 }}
          >
            {t('panel.orders.bulkAssignError', { message: errorMessage })}
          </p>
        )}

        {/* Sonuç özeti — hepsi atandıysa başarı; atama 0 ya da atlanan varsa uyarı stili
            (patron "tümü atlandı"yı başarı sanmasın; neden listelensin). */}
        {result && (
          <div
            aria-live="polite"
            data-testid="bulk-assign-result"
            style={{
              background: 'var(--page-bg)',
              borderRadius: 'var(--r-input)',
              padding: '12px 14px',
              marginBottom: 16,
              fontSize: 13.5,
            }}
          >
            <p
              data-testid="bulk-assign-result-summary"
              style={{
                margin: 0,
                color:
                  result.assigned.length === 0 || result.skipped.length > 0
                    ? 'var(--warning)'
                    : 'var(--success)',
                fontWeight: 600,
              }}
            >
              {t('panel.orders.bulkAssignAssigned', { n: result.assigned.length })}
            </p>
            {result.skipped.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 16, color: 'var(--text-secondary)' }}>
                {result.skipped.map((s) => (
                  <li key={s.orderId} style={{ marginBottom: 2 }}>
                    <span style={{ fontWeight: 600 }}>
                      {orderLabels?.[s.orderId] ?? t('panel.orders.bulkAssign.unknownOrder')}
                    </span>
                    {' — '}
                    {t(skipReasonKey(s.reason))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Butonlar */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={handleClose} disabled={isLoading}>
            {result ? t('common.close') : t('common.cancel')}
          </button>
          {!result && (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!selectedVehicleId || isLoading}
            >
              {isLoading ? t('common.loading') : t('common.confirm')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
